import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Dialog from 'material-ui/Dialog';
import { CardTitle } from 'material-ui/Card';
import Checkbox from 'material-ui/Checkbox';
import InputRange from 'react-input-range';
import 'react-input-range/lib/css/index.css';
import {
  daysDiff,
  getDatesRange,
  getInputRangeLabels,
  getInputRangeInit,
  dateToIso,
  getUtcStringWithoutHours,
  getUtcString,
  getDaysOfVacation,
  getDayLabel
} from './helpers';
import * as linearCalendarAction from './../../../data/linearCalendar/actions';
import { DefaultButton } from '../../Buttons';
import { httpStatus, calendar, time } from './../../../data/constants';
import { useLanguage } from '../../../context/LanguageContext';
import './styles.scss';

class VacationEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: 0,
      disabled: false,
      vacationStartDate: null,
      vacationEndDate: null,
      daysOfVacation: 0
    };

    this.handleClose = props.handleClose;

    this.activityId = props.activityId;
    this.vacationWindowStart = props.validatedVacationSlideStartDate;
    this.vacationWindowEnd = props.validatedVacationSlideEndDate;
    this.originalVacationStart = props.originalStartDate;
    this.originalVacationEnd =
      props.originalEndDate - time.ONE_DAY_IN_MILLISECONDS;
    this.vacationStartDate = props.startDate;
    this.vacationEndDate = props.endDate - time.ONE_DAY_IN_MILLISECONDS;

    this.bidSessionStartDate = new Date(
      getUtcStringWithoutHours(
        new Date(
          props.bidSessionStartDate[0],
          props.bidSessionStartDate[1] - 1,
          props.bidSessionStartDate[2]
        )
      )
    ).getTime();

    this.datesRange = getDatesRange(
      this.vacationWindowStart,
      this.vacationWindowEnd
    );
    this.inputRangeMin = 0;
    this.inputRangeMax = this.datesRange.length - 1;
    this.inputRangeLabels = getInputRangeLabels(this.datesRange);
    this.inputRangePreviousSelection = {};
    this.inputRangeInit = getInputRangeInit(
      getUtcString(this.vacationStartDate),
      getUtcString(this.vacationEndDate),
      this.datesRange
    );
    this.maxVacationDays = daysDiff(
      this.originalVacationStart,
      this.originalVacationEnd
    );

    this.state.value = this.inputRangeInit;
    this.state.vacationStartDate = this.vacationStartDate;
    this.state.vacationEndDate = this.vacationEndDate;

    if (props.startDate === props.endDate) {
      this.vacationStartDate = this.originalVacationStart;
      this.vacationEndDate = this.originalVacationEnd;
      this.inputRangeInit = getInputRangeInit(
        getUtcString(this.vacationStartDate),
        getUtcString(this.vacationEndDate),
        this.datesRange
      );

      this.state = {
        value: 0,
        disabled: true,
        vacationStartDate: null,
        vacationEndDate: null
      };
    }
  }

  componentDidMount() {
    const daysOfVacation = getDaysOfVacation(
      this.state.vacationStartDate,
      this.state.vacationEndDate,
      this.state.disabled
    );
    this.dayLabel = getDayLabel(daysOfVacation);
    this.setState({ daysOfVacation });
  }

  onChangeStart = value => {
    this.inputRangePreviousSelection = value;
  };

  onChangeComplete = inputRangeCurrentSelection => {
    const previousMin = this.inputRangePreviousSelection.min;
    const currentMin = inputRangeCurrentSelection.min;
    const currentMax = inputRangeCurrentSelection.max;
    const selectedVacationDays = currentMax - currentMin;
    let value = inputRangeCurrentSelection;
    if (selectedVacationDays > this.maxVacationDays) {
      const adjustToMin = currentMin - previousMin;
      let newMin = currentMin;
      let newMax = currentMax;

      if (adjustToMin) {
        newMax = newMin + this.maxVacationDays;
      } else {
        newMin = newMax - this.maxVacationDays;
      }

      value = { min: newMin, max: newMax };
    }

    this.setState({
      value,
      vacationStartDate: new Date(this.datesRange[value.min]).getTime(),
      vacationEndDate: new Date(this.datesRange[value.max]).getTime()
    });
  };

  handleCheck = () => {
    const nextCheckedState = !this.state.disabled;
    let value = this.inputRangeInit;
    let startDate = this.vacationStartDate;
    let endDate = this.vacationEndDate;
    if (nextCheckedState) {
      value = 0;
      startDate = null;
      endDate = null;
    }
    this.setState({
      value,
      disabled: nextCheckedState,
      vacationStartDate: startDate,
      vacationEndDate: endDate
    });
  };

  handleSave = () => {
    const { vacationSaveBody: data, afterSave } = this.props;
    if (this.state.disabled) {
      data.startDate = getUtcStringWithoutHours(this.vacationWindowStart);
      data.endDate = getUtcStringWithoutHours(this.vacationWindowStart);
    } else {
      data.startDate = getUtcString(this.state.vacationStartDate);
      data.endDate = getUtcString(
        this.state.vacationEndDate + time.ONE_DAY_IN_MILLISECONDS
      );
    }
    linearCalendarAction.default
      .activitySave(this.activityId, data)()
      .then(response => {
        if (response && response.status === httpStatus.OK) {
          afterSave(
            calendarLabels.vacation.SAVE_MESSAGE,
            response.data.activity
          );
        }
      })
      .catch(error => this.showErrorNotification(error));
  };

  showErrorNotification = error => {
    const { showNotification } = this.props;
    if (error.response && error.response.data && error.response.data.message) {
      showNotification(error.response.data.message);
    } else {
      showNotification('An error occurred. Please try again.');
    }
  };

  handleReset = () => {
    const startDateString = getUtcString(this.originalVacationStart);
    const endDateString = getUtcString(this.originalVacationEnd);
    const value = getInputRangeInit(
      startDateString,
      endDateString,
      this.datesRange
    );

    this.setState({
      value,
      disabled: false,
      vacationStartDate: this.originalVacationStart,
      vacationEndDate: this.originalVacationEnd
    });
  };

  formatLabel = labelIndex => {
    return this.inputRangeLabels[labelIndex];
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.vacationStartDate != this.state.vacationStartDate ||
      prevState.vacationEndDate != this.state.vacationEndDate ||
      prevState.disabled != this.state.disabled
    ) {
      const daysOfVacation = getDaysOfVacation(
        this.state.vacationStartDate,
        this.state.vacationEndDate,
        this.state.disabled
      );
      this.dayLabel = getDayLabel(daysOfVacation);
      this.setState({ daysOfVacation });
    }
  }

  render() {
    const actions = [
      <DefaultButton
        onClick={this.handleClose}
        className='btn-cancel'
      >{'Cancel'}</DefaultButton>,
      <DefaultButton
        onClick={this.handleSave}
        className='btn-ok'
      >{'Apply'}</DefaultButton>
    ];

    return (
      <Dialog
        title={
          <div className='no-margin'>
            <CardTitle
              title={calendarLabels.vacation.TITLE}
              subtitle={calendarLabels.vacation.SUBTITLE}
              className='vacation-editor-title'
            />
          </div>
        }
        actions={actions}
        modal={false}
        open
        onRequestClose={this.handleClose}
        className='vacation-editor'
        contentClassName='vacation-editor-container'
        actionsContainerClassName='vacation-editor-actions-container'
        bodyClassName='vacation-editor-content'
      >
        <div className='row vacation-editor-dates'>
          <div className='small-4 columns original'>
            Original dates
            <span>
              {` ${dateToIso(this.originalVacationStart)} — ${dateToIso(
                this.originalVacationEnd
              )}`}
            </span>
            <span className='reset' onClick={this.handleReset}>
              {calendar.vacation.RESET_BTN}
            </span>
          </div>
          <div className='small-4 small-offset-4 current'>
            Selected vacation {this.dayLabel}
            {this.state.daysOfVacation != 1 ? (
              <span>
                {` ${dateToIso(this.state.vacationStartDate)} — ${dateToIso(
                  this.state.vacationEndDate
                )}`}
              </span>
            ) : (
              <span>{` ${dateToIso(this.state.vacationStartDate)}`}</span>
            )}
            <span className='vacation-days'>{`${this.state.daysOfVacation} ${this.dayLabel}`}</span>
          </div>
        </div>
        <div className='row form'>
          <InputRange
            minValue={this.inputRangeMin}
            maxValue={this.inputRangeMax}
            disabled={this.state.disabled}
            formatLabel={labelIndex => this.formatLabel(labelIndex)}
            value={this.state.value}
            onChange={value => {
              this.setState({ value });
            }}
            onChangeStart={this.onChangeStart}
            onChangeComplete={this.onChangeComplete}
            allowSameValues={true}
          />
          <Checkbox
            className='checkbox'
            checked={this.state.disabled}
            label={calendarLabels.vacation.CHECK_BOX_LABEL}
            onClick={this.handleCheck}
          />
        </div>
      </Dialog>
    );
  }
}

VacationEditor.propTypes = {
  handleClose: PropTypes.func.isRequired,
  afterSave: PropTypes.func.isRequired,
  activityId: PropTypes.number.isRequired,
  validatedVacationSlideStartDate: PropTypes.number.isRequired,
  validatedVacationSlideEndDate: PropTypes.number.isRequired,
  originalStartDate: PropTypes.number.isRequired,
  originalEndDate: PropTypes.number.isRequired,
  startDate: PropTypes.number.isRequired,
  endDate: PropTypes.number.isRequired,
  bidSessionStartDate: PropTypes.array.isRequired,
  vacationSaveBody: PropTypes.object.isRequired,
  showNotification: PropTypes.func.isRequired
};

function VacationEditorWrapper(props) {
  const { labels } = useLanguage();
  return <VacationEditor {...props} labels={labels} />;
}

export default VacationEditorWrapper;
